import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppComponent } from '@app/app.component';
import { EmptyStringValidator } from '@app/_helpers';
import { natureOfBusinesses, Partner, Signup } from '@app/_models';
import { AlertService, GeographyService, SignupService } from '@app/_services';
import { PartnerComponent } from '../partner.component';
import { Location } from '@angular/common';

@Component({
  selector: 'app-partner-start',
  templateUrl: './partner-start.component.html',
  styleUrls: ['./partner-start.component.css']
})
export class PartnerStartComponent implements OnInit {
  stepperPosition: string;
  hideStepper: boolean = false;
  innerWidth: number = window.innerWidth;
  form: UntypedFormGroup;
  loading: boolean = true;
  submitted = false
  submitDisabled = true;
  signup: Signup = new Signup();
  partner: Partner = new Partner();
  natureOfBusiness = natureOfBusinesses;
  getOtherNatureofBusiness: boolean = false;
  firstNameInvalid: boolean = false;
  lastNameInvalid: boolean = false;
  companyNameInvalid: boolean = false;
  clientnatureofbusinessInvalid: boolean = false;
  othernatureofbusinessInvalid: boolean = false;
  phoneNumberInvalid: boolean = false;
  emailInvalid: boolean = false;
  billToZipInvalid: boolean = false;
  employeeCount: string;
  partnerNameInvalid: boolean = false;
  numberOfEmployeesInvalid: boolean = false;
  partnerName: string;
  headerText: string;
  partnerDescription: string;
  id: number;
  returnUrl: string;
  token: string = null;
  errorMessage: string;
  tierID: number;
  partnerURL: string;
  sendToHubspot: boolean = false;
  staff: boolean = false;

  constructor(private formBuilder: UntypedFormBuilder,
    private router: Router,
    private signupService: SignupService,
    private geographyService: GeographyService,
    private alertService: AlertService,
    private partnerComponent: PartnerComponent,
    private appComponent: AppComponent,
    private location: Location,
    private activatedRoute: ActivatedRoute) {
  }

  ngOnInit(): void {
    this.handleAppComponent();
    this.buildForm();
    this.handleGetSignup();
    this.handleToken();
    this.loading = false;
  }

  ngAfterViewInit(): void {
    this.partnerComponent.handleStepper(this.hideStepper);
    this.partnerComponent.handleViewChange();
    this.partnerComponent.setStep('/partner');
    if (this.form.valid) {
      this.submitDisabled = false;
    }
    if (this.signup.othernatureofbusiness) {
      this.form.patchValue({ clientnatureofbusiness: 'Other', othernatureofbusiness: this.signup.othernatureofbusiness })
      this.toggleNatureOfBusiness(true);
    }
  }

  handleGetSignup() {
    this.signup = this.signupService.getSignup();
    if (this.signup.firstName != undefined) {
      this.handlePatchedValues();
    }
  }

  handleAppComponent() {
    this.appComponent.partner = true;
    this.setAppComponentElementsVisibility(true);
  }

  handleToken() {
    this.token = this.activatedRoute.snapshot.paramMap.get("token");
    this.signupService.handlePartnerToken(this.token).subscribe(response => this.handleTokenResponse(response));
  }

  handleTokenResponse(response: any): void {
    if (response.error) {
      this.parseTokenResponse(response.message);
    }
    else {
      this.partner = response;
      this.handlePartnerData(this.partner);
    }
  }

  handlePartnerData(data: any) {
    //saving partner to signup to make it fetchable in session storage
    this.signup.partner = this.partner;

    //local variables for display and form data
    this.tierID = data.partnerTierID;
    this.id = data.partnerID;
    this.appComponent.partnerLogo = this.partner.logoImageContentType + this.partner.logo;
    if (data.partnerName.includes(" ")) {
      this.partnerURL = data.partnerName.replace(" ", "");
    }
    else {
      this.partnerURL = data.partnerName;
    }
    this.location.replaceState(this.partnerURL);
    this.headerText = data.headerMessage;
    this.partnerName = data.partnerName;
    this.partnerDescription = data.partnerDescription;
    this.partnerComponent.loading = false;
    this.signup.partner.token = this.token;
    this.handleStaff();
  }

  handleStaff() {
    if (this.partner.partnerID === 4 && this.token === "47998235") {
      this.staff = true;
    }
  }

  handlePatchedValues() {
    this.token = this.signup.partner.token;
    this.form.patchValue({ partnerToken: this.token });
    this.form.patchValue({ partnerTierID: this.signup.partner.partnerTierID });
    this.form.patchValue({ partnerID: this.signup.partner.partnerID });
    this.form.patchValue({ firstName: this.signup.firstName });
    this.form.patchValue({ lastName: this.signup.lastName });
    this.form.patchValue({ companyName: this.signup.companyName });
    this.form.patchValue({ clientnatureofbusiness: this.signup.clientnatureofbusiness });
    this.form.patchValue({ phoneNumber: this.signup.phoneNumber });
    this.form.patchValue({ email: this.signup.email });
    this.form.patchValue({ billToZip: this.signup.billToZip });
    this.form.patchValue({ numberOfEmployees: this.signup.numberOfEmployees });
  }

  parseTokenResponse(message: string): void {
    var obj = JSON.parse(message);
    this.errorMessage = obj['error'];
  }

  toggleNatureOfBusiness(isOther: boolean) {
    if (this.form) {
      if (isOther) {
        this.form.controls.othernatureofbusiness.addValidators([Validators.required, EmptyStringValidator]);
      } else {
        this.form.controls.othernatureofbusiness.removeValidators([Validators.required, EmptyStringValidator]);
      }
      this.getOtherNatureofBusiness = isOther;
      this.form.controls.othernatureofbusiness.updateValueAndValidity();
    }
  }

  buildForm() {
    this.form = this.formBuilder.group({
      numberOfEmployees: [this.signup.numberOfEmployees, [Validators.required]],
      firstName: [this.signup.firstName, [Validators.required, Validators.pattern('[a-z A-Z  -\/:-@\[-\`{-~]+$'), EmptyStringValidator]],
      lastName: [this.signup.lastName, [Validators.required, Validators.pattern('[a-z A-Z  -\/:-@\[-\`{-~]+$'), EmptyStringValidator]],
      email: [this.signup.email, [Validators.required, Validators.email]],
      companyName: [this.signup.companyName, [Validators.required, EmptyStringValidator]],
      clientnatureofbusiness: [this.signup != null && this.signup.clientnatureofbusiness != undefined ? this.signup.clientnatureofbusiness : '', [Validators.required]],
      othernatureofbusiness: [''],
      phoneNumber: [this.signup.phoneNumber, [Validators.required, Validators.minLength(10), Validators.pattern(/^-?(0|[1-9]\d*)?$/)]],
      billToZip: [this.signup.billToZip, [Validators.required, Validators.minLength(5), Validators.pattern(/^-?(|[0-9]\d*)?$/)]],
      partner: this.partner,
      partnerID: this.partner.partnerID,
      partnerTierID: this.partner.partnerTierID
    });
  }

  onCancel() {
    this.router.navigate(['/account/login'])
  }

  onSubmit() {
    this.submitted = true;
    this.loading = true;

    // reset alerts on submit
    this.alertService.clear();

    if (this.form.invalid) {
      this.alertService.error("Please ensure your provided information is correct")
      this.loading = false;
      return;
    }

    if (this.getOtherNatureofBusiness) {
      this.form.patchValue({ clientnatureofbusiness: this.form.controls.othernatureofbusiness.value });
    }

    this.geographyService.lookupState(this.form.value.billToZip).subscribe(state => {

      if (state.code == undefined) {
        this.alertService.error("Make sure your zip code is valid!");
        this.loading = false;
        return;
      }
      else {
        this.form.patchValue({ partner: this.partner });
        this.form.patchValue({ partnerID: this.id });
        this.form.patchValue({ partnerTierID: this.partner.partnerTierID });
        if (this.form.value.numberOfEmployees == "1000-2499" || this.form.value.numberOfEmployees == "2500-4999" || this.form.value.numberOfEmployees == "5000-or-more") {
          this.sendToHubspot = true;
        }
        this.signupService.insertPartner(this.form.value).subscribe(response => {
          if (response === 0) {
            this.loading = false;
            this.alertService.error('An account exists with this email address. Please try <a href="/account/login">logging in.</a>')
          }
          else {
            this.form.value.ID = response
            this.signupService.saveStart(this.form.value, state.code);
            this.partnerComponent.startStep.visited = true;
            if (this.sendToHubspot) {
              this.router.navigate(['/partner/contact'])
            }
            else {
              this.router.navigate(['/partner/package']);
            }
          }
        })
      }
    });
  }

  showValidation(field: string) {
    if (this.form.controls[field].invalid && this.form.controls[field].dirty) {
      this[field + "Invalid"] = true;
    }
    else {
      this[field + "Invalid"] = false;
    }
  }

  setAppComponentElementsVisibility(val: boolean): void {
    this.appComponent.application.hasFooter = val;
    this.appComponent.showHelpCenter = val;
  }

}
